/**
 * ReferenceUpload — 다중 참조 사진 썸네일 뷰
 *
 * 역할별(신랑/신부) 사진 목록을 작은 썸네일로 표시.
 * - 업로드: '+' 버튼 → 파일 선택 → 프리뷰 Modal → 얼굴 감지 → 업로드
 * - 크게보기: 썸네일 클릭 → Modal (상단에 삭제 버튼)
 * - 삭제: Modal 내 삭제 버튼 → ConfirmDialog
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Spinner, Modal, ConfirmDialog, Button } from '@/components/ui'
import { apiFetch, getErrorMessage } from '@/lib/api'
import { useUIStore } from '@/stores/uiStore'
import type { PersonRole } from '@cugumi/shared/types'
import type { ReferencePhoto } from '../hooks/useReferencePhotos'
import styles from './ReferenceUpload.module.css'

const MAX_PHOTOS_PER_ROLE = 5

interface ReferenceUploadProps {
  photos: ReferencePhoto[]
  uploading: PersonRole | null
  onUpload: (file: File, role: PersonRole) => Promise<void>
  onDelete: (id: string) => Promise<void>
  getByRole: (role: PersonRole) => ReferencePhoto[]
}

const ROLE_LABELS: Record<'GROOM' | 'BRIDE', string> = {
  GROOM: '신랑',
  BRIDE: '신부',
}

type FaceDetectStatus = 'idle' | 'detecting' | 'ok' | 'error'

function PhotoStrip({
  role,
  photos,
  isUploading,
  onUpload,
  onDelete,
}: {
  role: 'GROOM' | 'BRIDE'
  photos: ReferencePhoto[]
  isUploading: boolean
  onUpload: (file: File, role: PersonRole) => Promise<void>
  onDelete: (id: string) => Promise<void>
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const addToast = useUIStore((s) => s.addToast)
  const [previewPhoto, setPreviewPhoto] = useState<ReferencePhoto | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // 업로드 프리뷰 상태
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [faceStatus, setFaceStatus] = useState<FaceDetectStatus>('idle')
  const [faceError, setFaceError] = useState<string | null>(null)
  const [uploadingFile, setUploadingFile] = useState(false)
  const previewUrlRef = useRef<string | null>(null)

  // 프리뷰 URL cleanup on unmount only
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
    }
  }, [])

  const resetUploadPreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = null
    }
    setShowUploadModal(false)
    setPendingFile(null)
    setPreviewUrl(null)
    setFaceStatus('idle')
    setFaceError(null)
    setUploadingFile(false)
  }, [])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
      const url = URL.createObjectURL(file)
      previewUrlRef.current = url
      setPendingFile(file)
      setPreviewUrl(url)
      setFaceStatus('idle')
      setFaceError(null)
    }
    e.target.value = ''
  }

  const handleFaceDetect = async () => {
    if (!pendingFile) return
    setFaceStatus('detecting')
    setFaceError(null)

    const formData = new FormData()
    formData.append('image', pendingFile)

    try {
      const result = await apiFetch<{ faceDetected: boolean }>(
        '/api/ai/face-detect',
        { method: 'POST', body: formData },
      )
      if (result.success) {
        setFaceStatus('ok')
      } else {
        setFaceStatus('error')
        setFaceError(getErrorMessage(result.error, '얼굴이 감지되지 않았습니다'))
      }
    } catch {
      setFaceStatus('error')
      setFaceError('얼굴 감지에 실패했습니다. 다시 시도해주세요')
    }
  }

  const handleUploadConfirm = async () => {
    if (!pendingFile) return
    setUploadingFile(true)
    try {
      await onUpload(pendingFile, role)
      addToast({ type: 'success', message: '참조 사진이 업로드되었습니다' })
      resetUploadPreview()
    } catch (err) {
      const msg = err instanceof Error ? err.message : '업로드에 실패했습니다'
      addToast({ type: 'error', message: msg })
      setFaceError(msg)
      setFaceStatus('error')
    } finally {
      setUploadingFile(false)
    }
  }

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async () => {
    if (!previewPhoto) return
    setIsDeleting(true)
    try {
      await onDelete(previewPhoto.id)
      addToast({ type: 'success', message: '참조 사진이 삭제되었습니다' })
      setPreviewPhoto(null)
      setShowDeleteConfirm(false)
    } finally {
      setIsDeleting(false)
    }
  }

  const canAdd = photos.length < MAX_PHOTOS_PER_ROLE

  return (
    <div className={styles.strip}>
      <div className={styles.stripHeader}>
        <span className={styles.stripLabel}>{ROLE_LABELS[role]} 참조 사진</span>
        <span className={styles.stripCount}>({photos.length}/{MAX_PHOTOS_PER_ROLE})</span>
      </div>

      <div className={styles.thumbList}>
        {photos.map((photo) => (
          <button
            key={photo.id}
            type="button"
            className={styles.thumb}
            onClick={() => setPreviewPhoto(photo)}
          >
            <img src={photo.originalUrl} alt={ROLE_LABELS[role]} className={styles.thumbImg} />
            <span className={photo.faceDetected ? `${styles.faceBadge} ${styles.faceOk}` : `${styles.faceBadge} ${styles.faceWarn}`}>
              {photo.faceDetected ? '✓' : '!'}
            </span>
          </button>
        ))}

        {canAdd && (
          <button
            type="button"
            className={styles.addBtn}
            onClick={() => setShowUploadModal(true)}
            disabled={isUploading}
          >
            {isUploading ? <Spinner size="sm" /> : <span className={styles.addIcon}>+</span>}
          </button>
        )}
      </div>

      {/* 업로드 프리뷰 Modal */}
      <Modal
        open={showUploadModal}
        onClose={resetUploadPreview}
        title={`${ROLE_LABELS[role]} 참조 사진 업로드`}
        size="md"
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="업로드 프리뷰"
            className={styles.uploadPreviewImg}
          />
        ) : (
          <div className={styles.uploadPlaceholder} onClick={() => inputRef.current?.click()}>
            <span className={styles.uploadPlaceholderIcon}>+</span>
            <p className={styles.uploadPlaceholderHint}>클릭하여 사진 선택</p>
            <p className={styles.uploadPlaceholderHint}>JPG, PNG, WebP (최대 10MB)</p>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className={styles.hiddenInput}
          onChange={handleFileChange}
        />

        {faceStatus === 'detecting' && (
          <div className={styles.faceStatus}>
            <Spinner size="sm" /> 얼굴 감지 중...
          </div>
        )}

        {faceStatus === 'ok' && (
          <div className={`${styles.faceStatus} ${styles.faceStatusOk}`}>
            ✓ 얼굴이 감지되었습니다
          </div>
        )}

        {faceStatus === 'error' && (
          <div className={`${styles.faceStatus} ${styles.faceStatusError}`}>
            ✗ {faceError}
          </div>
        )}

        {pendingFile && (
          <div className={styles.uploadActions}>
            <Button variant="ghost" size="sm" onClick={() => inputRef.current?.click()} disabled={faceStatus === 'detecting' || uploadingFile}>
              다른 사진
            </Button>

            {faceStatus === 'ok' ? (
              <Button size="sm" onClick={handleUploadConfirm} disabled={uploadingFile}>
                {uploadingFile ? <><Spinner size="sm" /> 업로드 중...</> : '업로드'}
              </Button>
            ) : (
              <Button size="sm" onClick={handleFaceDetect} disabled={faceStatus === 'detecting'}>
                {faceStatus === 'detecting' ? <><Spinner size="sm" /> 감지 중...</> : '얼굴 감지'}
              </Button>
            )}
          </div>
        )}
      </Modal>

      {/* 크게보기 Modal */}
      <Modal
        open={!!previewPhoto}
        onClose={() => setPreviewPhoto(null)}
        title={`${ROLE_LABELS[role]} 참조 사진`}
        size="md"
        headerExtra={
          <Button variant="danger" size="sm" onClick={handleDeleteClick}>
            삭제
          </Button>
        }
      >
        {previewPhoto && (
          <img
            src={previewPhoto.originalUrl}
            alt={ROLE_LABELS[role]}
            className={styles.previewImg}
          />
        )}
      </Modal>

      {/* 삭제 확인 */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title="사진 삭제"
        message="이 참조 사진을 삭제하시겠습니까?"
        confirmLabel="삭제"
        variant="danger"
        loading={isDeleting}
      />
    </div>
  )
}

export function ReferenceUpload({ photos, uploading, onUpload, onDelete, getByRole }: ReferenceUploadProps) {
  return (
    <div className={styles.container}>
      <PhotoStrip
        role="GROOM"
        photos={getByRole('GROOM')}
        isUploading={uploading === 'GROOM'}
        onUpload={onUpload}
        onDelete={onDelete}
      />
      <PhotoStrip
        role="BRIDE"
        photos={getByRole('BRIDE')}
        isUploading={uploading === 'BRIDE'}
        onUpload={onUpload}
        onDelete={onDelete}
      />
    </div>
  )
}
